"use client";

import { useCallback, useEffect } from "react";
import { atom, useAtom } from "jotai";
import { toast } from "sonner";
import {
  Task,
  TaskCategory,
  TaskEffort,
  TaskImpact,
  TaskStatus,
  ActivityType,
} from "@constructr-sdk/entities";
import { trpcReactQueryApi } from "~/trpc/react";
import type {
  UseTaskDetailsParams,
  UseTaskDetailsReturn,
} from "~/modules/main/hooks/use-task-details/use-task-details.interface";

// Global state atoms - hoisted to top level to prevent recreation on each render
const taskAtom = atom<Task | null>(null);
const projectIdAtom = atom<string | null>(null);
const projectProgressPercentAtom = atom<number | null>(null);
const notesDraftAtom = atom<string>("");
const isLoadingAtom = atom<boolean>(false);
const isSavingAtom = atom<boolean>(false);
const errorAtom = atom<string | null>(null);

/**
 * Custom domain hook for managing task details.
 * Provides state and commands for viewing and editing a single task.
 */
export function useTaskDetails(params: UseTaskDetailsParams): UseTaskDetailsReturn {
  // Global state
  const [task, setTask] = useAtom(taskAtom);
  const [projectId, setProjectId] = useAtom(projectIdAtom);
  const [projectProgressPercent, setProjectProgressPercent] = useAtom(projectProgressPercentAtom);
  const [notesDraft, setNotesDraft] = useAtom(notesDraftAtom);
  const [isLoading, setIsLoading] = useAtom(isLoadingAtom);
  const [isSaving, setIsSaving] = useAtom(isSavingAtom);
  const [error, setError] = useAtom(errorAtom);

  // TRPC utils for cache invalidation
  const utils = trpcReactQueryApi.useUtils();

  // Mutations
  const updateTaskMutation = trpcReactQueryApi.main.tasks.update.useMutation({
    onSuccess: async () => {
      toast.success("Task updated successfully");
      await utils.main.tasks.byId.invalidate();
      await utils.main.tasks.list.invalidate();
      await utils.main.kpis.getProjectKpis.invalidate();
      await utils.main.kpis.getCategoryBreakdown.invalidate();
    },
    onError: (err) => {
      toast.error(err.message || "Failed to update task");
      setError(err.message || "Failed to update task");
    },
  });

  const deleteTaskMutation = trpcReactQueryApi.main.tasks.delete.useMutation({
    onSuccess: async () => {
      toast.success("Task deleted successfully");
      await utils.main.tasks.list.invalidate();
      await utils.main.kpis.getProjectKpis.invalidate();
      await utils.main.kpis.getCategoryBreakdown.invalidate();
    },
    onError: (err) => {
      toast.error(err.message || "Failed to delete task");
      setError(err.message || "Failed to delete task");
    },
  });

  const createActivityMutation = trpcReactQueryApi.main.activities.create.useMutation();

  const toggleCompletedMutation = trpcReactQueryApi.main.tasks.toggleCompleted.useMutation({
    onSuccess: async (updatedTask) => {
      toast.success(
        updatedTask.status === TaskStatus.COMPLETED
          ? "Task marked as completed"
          : "Task reopened"
      );
      setTask(updatedTask);
      await utils.main.tasks.byId.invalidate();
      await utils.main.tasks.list.invalidate();
      await utils.main.kpis.getProjectKpis.invalidate();
      await utils.main.kpis.getCategoryBreakdown.invalidate();
      
      // Create activity record
      if (projectId) {
        await utils.main.activities.invalidate();
      }
    },
    onError: (err) => {
      toast.error(err.message || "Failed to update task status");
      setError(err.message || "Failed to update task status");
    },
  });

  /**
   * Initialize the view by loading task and related project progress by task id.
   */
  const initialize = useCallback(
    async (taskId: string) => {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch task details
        const taskData = await utils.main.tasks.byId.fetch({ id: taskId });
        
        if (!taskData) {
          throw new Error("Task not found");
        }

        setTask(taskData);
        setProjectId(taskData.projectId);
        setNotesDraft(taskData.description || "");

        // Fetch project details to get progress
        const projectData = await utils.main.projects.byId.fetch({
          id: taskData.projectId,
        });

        if (projectData) {
          setProjectProgressPercent(projectData.percentComplete);
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to load task details";
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [utils, setTask, setProjectId, setNotesDraft, setProjectProgressPercent, setIsLoading, setError]
  );

  /**
   * Update an existing task by id.
   * Accepts recommendedActions as a separate field for granular, actionable steps.
   */
  const updateTask = useCallback(
    async (
      id: string,
      title: string,
      description: string | null,
      category: TaskCategory,
      impact: TaskImpact,
      effort: TaskEffort | null,
      relatedUrl: string | null,
      sourceLabel: string,
      status: TaskStatus,
      recommendedActions: string[] | null,
    ) => {
      setIsSaving(true);
      setError(null);

      try {
        const updatedTask = await updateTaskMutation.mutateAsync({
          id,
          title,
          description,
          category,
          impact,
          effort,
          relatedUrl,
          sourceLabel,
          status,
          recommendedActions,
        });

        setTask(updatedTask);
        setNotesDraft(updatedTask.description || "");

        // Create activity record for task edit
        if (projectId) {
          await createActivityMutation.mutateAsync({
            projectId,
            type: ActivityType.TASK_EDITED,
            description: `Task "${title}" was updated`,
          });
        }
      } catch (err) {
        // Error handling is done in mutation callbacks
      } finally {
        setIsSaving(false);
      }
    },
    [updateTaskMutation, setTask, setNotesDraft, setIsSaving, setError, projectId, createActivityMutation]
  );

  /**
   * Set the task status (OPEN/COMPLETED).
   */
  const setTaskStatus = useCallback(
    async (id: string, status: TaskStatus) => {
      setIsSaving(true);
      setError(null);

      try {
        await toggleCompletedMutation.mutateAsync({ id });
      } catch (err) {
        // Error handling is done in mutation callbacks
      } finally {
        setIsSaving(false);
      }
    },
    [toggleCompletedMutation, setIsSaving, setError]
  );

  /**
   * Persist custom notes for the task.
   */
  const saveNotes = useCallback(
    async (taskId: string, notes: string) => {
      if (!task) return;

      setIsSaving(true);
      setError(null);

      try {
        const updatedTask = await updateTaskMutation.mutateAsync({
          id: taskId,
          title: task.title,
          description: notes,
          category: task.category,
          impact: task.impact,
          effort: task.effort,
          relatedUrl: task.relatedUrl,
          sourceLabel: task.source.label,
          status: task.status,
          recommendedActions: task.recommendedActions,
        });

        setTask(updatedTask);
        setNotesDraft(updatedTask.description || "");
        toast.success("Notes saved successfully");
      } catch (err) {
        // Error handling is done in mutation callbacks
      } finally {
        setIsSaving(false);
      }
    },
    [task, updateTaskMutation, setTask, setNotesDraft, setIsSaving, setError]
  );

  /**
   * Delete the task by id.
   */
  const deleteTask = useCallback(
    async (id: string) => {
      setIsSaving(true);
      setError(null);

      try {
        await deleteTaskMutation.mutateAsync({ id });
        
        // Clear local state after successful deletion
        setTask(null);
        setProjectId(null);
        setProjectProgressPercent(null);
        setNotesDraft("");
      } catch (err) {
        // Error handling is done in mutation callbacks
      } finally {
        setIsSaving(false);
      }
    },
    [deleteTaskMutation, setTask, setProjectId, setProjectProgressPercent, setNotesDraft, setIsSaving, setError]
  );

  /**
   * Reset the notes draft to the last persisted value.
   */
  const resetNotesDraft = useCallback(() => {
    if (task) {
      setNotesDraft(task.description || "");
    }
  }, [task, setNotesDraft]);

  // Auto-load on mount if autoLoad is true and taskId is provided
  useEffect(() => {
    if (params.autoLoad && params.taskId) {
      void initialize(params.taskId);
    }
  }, [params.autoLoad, params.taskId, initialize]);

  return {
    // State
    task,
    projectId,
    projectProgressPercent,
    notesDraft,
    isLoading,
    isSaving,
    error,

    // Mutations
    setNotesDraft,
    resetNotesDraft,

    // Commands
    initialize,
    updateTask,
    setTaskStatus,
    saveNotes,
    deleteTask,
  };
}